import { useCallback, useRef } from 'react'

import { useRoomContext } from '@/src/context'
import { EReaction } from '@/src/constants/reactions'
import { handlePublishData } from '@/src/helpers/handlePublishData'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import {
  selectCurrentRaiseHand,
  setCurrentRaiseHand,
  setParticipantHand,
  addParticipantEphemeralReaction,
} from '@/src/store/slices/reactions'
import { selectRoomState } from '@/src/store/slices/room'

interface UseReactionsOptions {
  /** When true, raise-hand uses only the server API (no data channel / no canPublishData needed). */
  apiOnly?: boolean
}

export const useReactions = (options?: UseReactionsOptions) => {
  const apiOnly = options?.apiOnly ?? false
  const lastReactionTime = useRef(0)

  const dispatch = useAppDispatch()
  const room = useRoomContext()
  const { livekit_url } = useAppSelector(selectRoomState)

  const isHandRaised = useAppSelector(selectCurrentRaiseHand)

  const handleRaiseHandToggle = useCallback(() => {
    const newValue = !isHandRaised
    dispatch(setCurrentRaiseHand(newValue))

    const myIdentity = room?.localParticipant?.identity
    if (myIdentity) {
      dispatch(setParticipantHand({ identity: myIdentity, isHandRaised: newValue }))
    }

    if (!apiOnly) {
      handlePublishData(
        room,
        {
          type: 'raiseHand',
          isHandRaised: newValue,
          participant: myIdentity,
        },
        {
          reliable: true,
          topic: 'reaction-topic',
        },
      )
    }

    if (livekit_url && myIdentity && room?.name) {
      fetch(`${window.origin}/api/raise_hand?livekit_url=${livekit_url}`, {
        method: 'POST',
        body: JSON.stringify({
          room: room.name,
          identity: myIdentity,
          isHandRaised: newValue,
        }),
      }).catch((err) => {
        console.error('Failed to update raise hand via API:', err)
      })
    }
  }, [dispatch, isHandRaised, room, livekit_url, apiOnly])

  const handleQuickReaction = useCallback(
    (reaction: keyof typeof EReaction) => {
      const now = Date.now()
      if (now - lastReactionTime.current < 300) return
      lastReactionTime.current = now

      const myIdentity = room?.localParticipant?.identity
      if (!myIdentity) return

      const reactionId = Math.random().toString()

      dispatch(
        addParticipantEphemeralReaction({
          identity: myIdentity,
          ephemeral: {
            id: reactionId,
            type: reaction,
          },
        }),
      )

      handlePublishData(
        room,
        {
          type: 'ephemeral',
          reactionId,
          reaction,
          participant: myIdentity,
        },
        {
          reliable: true,
          topic: 'reaction-topic',
        },
      )
    },
    [dispatch, room],
  )

  return { handleRaiseHandToggle, handleQuickReaction, isHandRaised }
}
